import { Injectable } from "@angular/core";
import { NmsWizardStepMapData } from "@nms-angular-toolkit/nms-wizard";
import { SlotData } from "../wizard/steps/1-ends/end-step-circuit-dwdm.model";
import { EndsStepModel } from "../wizard/steps/1-ends/ends-step.model";
import { TranspondersStepModel, TransponderPort, ChannelData } from "../wizard/steps/2-transponders/transponders-step.model";
import { IntermediatesStepModel } from "../wizard/steps/3-intermediates/intermediates-step.model";
import { LinksStepModel } from "../wizard/steps/4-links/links-step.model";
import { AmplifiersStepModel, LosAction } from "../wizard/steps/5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel, CircuitPortIdentifierData } from "../wizard/steps/6-osc-management/osc-management-step.model";
import {
    DwdmCircuitData,
    GeneralStepModel,
    DwdmCircuitAmplifierData,
    DwdmCircuitOscData,
    DwdmCircuitEndData,
} from "../wizard/steps/7-general/general-step.model";

/**
 * Serviço responsável por converter os dados de @see NmsWizardStepMapData para @see DwdmCircuitData.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmCircuitWizardConverterService {
    public convertCacheMapToCircuitData(wizardStepsCacheMap: NmsWizardStepMapData): DwdmCircuitData {
        const endsStepModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        const transpondersStepModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        const intermediatesStepModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        const amplifiersStepModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        const linksStepModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        const oscManagementStepModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        const generalStepModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        const circuitData: DwdmCircuitData = {
            aEnd: this.getAEndStepData(endsStepModel, transpondersStepModel),
            zEnd: this.getZEndStepData(endsStepModel, transpondersStepModel),
            circuitName: generalStepModel.name,
            autoGeneratedName: generalStepModel.generatedName,
            circuitDescription: generalStepModel.description,
            linksResourcesIds: linksStepModel.links.map((link) => link.id),
            oscs: this.convertOsc(oscManagementStepModel),
            amplifiers: this.getAmplifiers(amplifiersStepModel),
            intermediatesResourcesIds: intermediatesStepModel.useIntermediateDevices
                ? intermediatesStepModel.devices.map((intermediate) => intermediate.resourceId)
                : []
        };

        return circuitData;
    }

    private getAmplifiers(amplifiersStepModel: AmplifiersStepModel): Array<DwdmCircuitAmplifierData> {
        return amplifiersStepModel.configuredAmplifiers.map(amp => {
            return {
                value : parseFloat(amp.value),
                limit : parseFloat(amp.limit),
                losThreshold : parseFloat(amp.losThreshold),
                losAction : amp.losAction === LosAction.ENABLED,
                amplifierPort : amp.portIdentifier,
                circuitAmplifierModeTypeData : amp.mode,
                circuitAmplifierProfileData : amp.profile
            }
        })
    }

    private convertOsc(oscManagementStepModel: OscManagementStepModel): DwdmCircuitOscData[] {
        if (!oscManagementStepModel.useOscManagement) {
            oscManagementStepModel.oscManagementPorts = [];
        }

        return oscManagementStepModel.oscManagementPorts.map((osc) => {
            return {
                linkRx: osc.linkRxId,
                linkTx: osc.linkTxId,
                oscPort: {
                    chassisIdentifier: osc.oscChassisIdentifier,
                    deviceResourceId: osc.endId,
                    portModelIdentifier: osc.oscPortModelIdentifier,
                    portNo: osc.oscPortNo,
                    slotIdentifier: osc.oscSlotIdentifier
                }
            };
        });
    }

    private getZEndStepData(endStep: EndsStepModel, transpondersStepModel: TranspondersStepModel): DwdmCircuitEndData {
        return {
            muxEnabled: endStep.zEndHasMux,
            muxResourceId: endStep.zEndHasMux ? endStep.zEndMux.resourceId : null,
            endConfigurationsData: transpondersStepModel.zEndPorts.map((port) => {
                return {
                    channel: this.extractChannel(port.channel),
                    multirate: port.multirate,
                    endPort: this.getEndPort(endStep.zEndSlots, port, endStep.zEndDevice.id),
                    txPower: port.txPower
                };
            })
        };
    }

    private getAEndStepData(endStep: EndsStepModel, transpondersStepModel: TranspondersStepModel): DwdmCircuitEndData {
        return {
            muxEnabled: endStep.aEndHasMux,
            muxResourceId: endStep.aEndHasMux ? endStep.aEndMux.resourceId : null,
            endConfigurationsData: transpondersStepModel.aEndPorts.map((port) => {
                return {
                    channel: this.extractChannel(port.channel),
                    multirate: port.multirate,
                    endPort: this.getEndPort(endStep.aEndSlots, port, endStep.aEndDevice.id),
                    txPower: port.txPower
                };
            })
        };
    }

    private getEndPort(slotsData: Array<SlotData>, port: TransponderPort, deviceResourceId: number): CircuitPortIdentifierData {
        const slot = slotsData.find((slot) => slot.ports.find((slotPort) => slotPort.portName === port.portName));

        return {
            chassisIdentifier: slot.chassisNo,
            deviceResourceId,
            portModelIdentifier: port.portModel,
            portNo: parseInt(port.portNo),
            slotIdentifier: slot.slotNo
        };
    }

    private extractChannel(rawChannel: string): ChannelData {
        const channelParts = rawChannel.replace("(", "").split(" ");
        const channel = parseFloat(channelParts[0]);
        const frequency = parseFloat(channelParts[1]);
        const waveLength = parseFloat(channelParts[3]);

        return { channel, frequency, waveLength };
    }
}
